import { dom } from '@omegagrid/core';
import rp, { AppComponent, Application, ParsedComponentId, GridEditor } from '@reporting/rp-app-host';
import { ModuleCode } from "@reporting/rp-types/accounting";
import { AccountingOptions } from '@reporting/rp-types/core';
import { Sidebar } from './components';
import { DimensionGridEditorAdapter, DimAttributesGridEditorAdapter, AttributesGridEditorAdapter,
	DrillColumnsGridEditorAdapter, DetailCustomGridEditorAdapter, SecurityRoleGridEditorAdapter } from './adapters';
import { ATTRIBUTE_PROP_NAMES } from "./types";
import * as finance from './factories.finance';
import * as items from './factories.items';
import * as general from './factories.general';

const createMainSidebar = (app: Application, componentId: ParsedComponentId) => {
	return dom.createElement<Sidebar>('rp-definition-sidebar');
}

const createDimensionGrid = (app: Application, componentId: ParsedComponentId) => {
	const module = componentId.ids[1] as ModuleCode;
	if (!module) return null;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new DimensionGridEditorAdapter(module);
	return component;
}

const createAttributesGridEditor = (app: Application, componentId: ParsedComponentId) => {
	const propName = ATTRIBUTE_PROP_NAMES[componentId.ids[1]];
	if (!propName) return null;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new AttributesGridEditorAdapter(propName as keyof AccountingOptions);
	return component;
}

const createDrillColumnsGridEditor = (app: Application, componentId: ParsedComponentId) => {
	const module = componentId.ids[1] as ModuleCode;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new DrillColumnsGridEditorAdapter(module);
	return component;
}

const createDetailCustomGridEditor = (app: Application, componentId: ParsedComponentId) => {
	const module = componentId.ids[1] as ModuleCode;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new DetailCustomGridEditorAdapter(module);
	return component;
}

const createDimAttributesGridEditor = (app: Application, componentId: ParsedComponentId) => {
	const module = componentId.ids[1] as ModuleCode;
	const dim_num = parseInt(componentId.ids[2]);
	if (!module || !dim_num) return null;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new DimAttributesGridEditorAdapter(module, dim_num);
	return component;
}

export const createSecurityRoleGridEditor = (app: Application, componentId: ParsedComponentId) => {
	const module = componentId.ids[1] as ModuleCode;
	const a = componentId.ids[2] ? parseInt(componentId.ids[2]) : rp.accountingProvider.a;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new SecurityRoleGridEditorAdapter(module, a);
	return component;
}

export const componentFactories = new Map<string, (app: Application, componentId: ParsedComponentId) => AppComponent|Promise<AppComponent>>([
	['definition', createMainSidebar], // sidebar
	['detailCustom', createDetailCustomGridEditor],
	['drillColumns', createDrillColumnsGridEditor],
	['dimensions', createDimensionGrid],
	['attributes', createAttributesGridEditor],
	['dimAttributes', createDimAttributesGridEditor],
	['securityRoles', createSecurityRoleGridEditor],
	['finReports', finance.createFinReportsGridEditor],
	['finAggregate', finance.createFinAggregateGridEditor],
	['finAggregatedRows', finance.createFinAggregatedRowsGridEditor],
	['finTree', finance.createFinTreeGridEditor],
	['finTreeCopy', finance.createFinTreeCopyForm],
	['finPeriodClosing', finance.createFinPeriodClosingContainer],
	['finAccounts', finance.createFinAccountsContainer],
	['finAccountsDelete', finance.createFinAccountsDeleteForm],
	['finAccountsGrid', finance.createFinAccountsGrid],
	['finAccountsGridEditor', finance.createFinAccountsGridEditor],
	['finReportForm', finance.createFinReportForm],
	['finReportCopy', finance.createFinReportCopyForm],
	['finExport', finance.createFinExportForm],
	['statementRows', finance.createFinRowsGridEditor],
	['finSync', finance.createSyncEditor],
	['fiscalYears', finance.createFiscalYearGrid],
	['scenarios', finance.createScenarioGrid],
	['reportStats', finance.createReportStatsGrid],
	['reportRecovery', finance.createReportRecoveryGrid],
	['itemIndicators', items.createItemIndicatorsContainer],
	['itemIndicatorsGridEditor', items.createItemIndicatorsGridEditor],
	['translations', general.createTranslationsGrid],
	['styles', general.createStylesGrid],
	['tableStyleEditor', general.createTableStyleEditor],
	// id of the former preview tab, kept so that saved layouts still open (as the editor)
	['tableStylePreview', general.createTableStyleEditor],
]);
